import { ChatMarkdown } from '@/modules/common/Chat/components/ChatMarkdown';
import {
  InteractiveMessageActionConfig,
  InteractiveMessageCard,
} from '@/modules/household/chat/components/messages/interactive/InteractiveMessageCard';
import { HouseholdChatMessage } from '@/modules/household/chat/contexts/householdChat.context';
import { MessageResponseAction } from '@/types/schema';

import { ApplicationRecordChangesConfirmationContent } from './applicationRecordChangesConfirmationContent/ApplicationRecordChangesConfirmationContent';
import { getApplicationRecordChangesSubtitle } from './applicationRecordChangesConfirmationContent/ApplicationRecordChangesConfirmationContent.utils';

interface ConfirmationMessageRendererProps {
  message: HouseholdChatMessage;
}

/**
 * The confirmation card's button presentation. The backend declares no
 * options for CONFIRMATION_MESSAGE — the labels here mirror its ResponseText
 * labels so the buttons and the persisted response row read the same.
 */
const CONFIRMATION_ACTIONS: Record<
  MessageResponseAction,
  InteractiveMessageActionConfig
> = {
  [MessageResponseAction.Dismiss]: { label: 'Cancel', variant: 'secondary' },
  [MessageResponseAction.Submit]: { label: 'Confirm', variant: 'primary' },
};

/** Resolution wording: "{Person} approved" / "{Person} declined" */
const CONFIRMATION_RESPONDED_VERBS: Record<MessageResponseAction, string> = {
  [MessageResponseAction.Submit]: 'approved',
  [MessageResponseAction.Dismiss]: 'declined',
};

/**
 * Renders a CONFIRMATION_MESSAGE row as an interactive message card whose
 * body is the proposed action's markdown summary.
 */
export function ConfirmationMessageRenderer({
  message,
}: ConfirmationMessageRendererProps) {
  const confirmationContent = message.confirmationContent;
  if (!confirmationContent) {
    // The card's typed content failed to resolve (it is decrypted
    // server-side); without the card's title there is nothing to confirm, so
    // fall back to the markdown summary like a plain assistant message.
    return <ChatMarkdown markdown={message.content} />;
  }

  const changes = confirmationContent.applicationRecordChanges;

  return (
    <InteractiveMessageCard
      message={message}
      title={confirmationContent.title}
      subtitle={changes ? getApplicationRecordChangesSubtitle(changes) : null}
      actions={CONFIRMATION_ACTIONS}
      respondedVerbs={CONFIRMATION_RESPONDED_VERBS}
    >
      {changes ? (
        <ApplicationRecordChangesConfirmationContent
          changes={changes}
          warnings={confirmationContent.warnings}
        />
      ) : (
        <ChatMarkdown markdown={message.content} />
      )}
    </InteractiveMessageCard>
  );
}
